import React from 'react';
import styled from 'styled-components';
import { Button } from '../../ui/Button';

/**
 * EmotionalBanner — the brand's emotional payoff line, placed to bookend the
 * first-time homepage (the hero opens, the feature beats teach, this closes
 * before the testimonial proof).
 *
 * Copy is the long-standing "You love them. Let it show." line, set over the
 * warm dinner-table/cake photo that used to sit behind the hero (Kate
 * 2026-06-04 — the image found a better home here, away from the hero text).
 */

const IMAGE = '/landing-assets/hero-bg.jpg';

const Band = styled.section`
  position: relative;
  /* Warm clay→ink fallback so the band never looks broken if the asset is
     absent (mirrors the old hero fallback). */
  background:
    linear-gradient(135deg, ${({ theme }) => theme.color.clay} 0%, ${({ theme }) => theme.color.inkSoft} 100%);
  background-size: cover;
  background-position: center;
  overflow: hidden;
`;

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${IMAGE});
  background-size: cover;
  background-position: center;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.32) 0%,
    rgba(0, 0, 0, 0.48) 100%
  );
`;

/* THE-870: ~20% shorter band at both widths, by cutting vertical padding only
   — the heading/CTA keep their size. 88px -> 54px (390px measured: 339px ->
   271px) and 132px -> 85px (1440px measured: 472px -> 378px). Padding has to
   drop further than 20% because the heading/button height inside is fixed;
   only trimming it by the same 20% would undershoot the total. */
const Inner = styled.div`
  position: relative;
  max-width: 720px;
  margin: 0 auto;
  padding: 54px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (min-width: 768px) {
    padding: 85px 32px;
  }
`;

const Heading = styled.h2<{ $compact?: boolean }>`
  font-family: ${({ theme }) => theme.font.sans};
  color: #ffffff;
  font-weight: ${({ $compact }) => ($compact ? 700 : 800)};
  font-size: ${({ $compact }) => ($compact ? '26px' : '32px')};
  line-height: 1.15;
  letter-spacing: -0.025em;
  margin: 0;
  text-shadow: 0 1px 16px rgba(0, 0, 0, 0.45);
  @media (min-width: 768px) {
    font-size: ${({ $compact }) => ($compact ? '28px' : '48px')};
  }
`;

const HeadingLine = styled.span`
  display: block;
`;

const CtaWrap = styled.div`
  margin-top: 36px;
  @media (min-width: 768px) {
    margin-top: 44px;
  }
`;

export interface EmotionalBannerProps {
  /** Two-line heading. Defaults to the long-standing brand payoff line;
   *  audience-targeted homepage variants (e.g. /moms) pass their own. */
  headline?: [string, string];
  ctaLabel?: string;
  onCtaClick?: () => void;
  /**
   * Render the CTA yourself instead of the default clay `Button`, given the
   * resolved label and handler — mirrors HomepageHero's `ctaSlot`. THE-1081:
   * the gift-guides hub preview's "Get started" becomes the white-on-photo
   * form of the hub's one primary button (HubFrameParts.tsx), not a second
   * pill style.
   */
  ctaSlot?: (label: string, onClick?: () => void) => React.ReactNode;
  /**
   * THE-1081: the banner headline at section-title size (28px desktop /
   * 26px phone, weight 700) instead of hero size (48px/32px, weight 800) —
   * "the banner uses the section-title size, not hero size." Opt-in so
   * every other caller (today's production homepage, /moms) renders
   * byte-identically.
   */
  compact?: boolean;
}

export const EmotionalBanner: React.FC<EmotionalBannerProps> = ({
  headline = ['You love them.', 'Let it show.'],
  ctaLabel = 'Get started',
  onCtaClick,
  ctaSlot,
  compact,
}) => (
  <Band aria-labelledby="emotional-banner-heading">
    <BgImage aria-hidden="true" />
    <Overlay aria-hidden="true" />
    <Inner>
      <Heading id="emotional-banner-heading" $compact={compact}>
        <HeadingLine>{headline[0]}</HeadingLine>
        <HeadingLine>{headline[1]}</HeadingLine>
      </Heading>
      <CtaWrap>
        {ctaSlot
          ? ctaSlot(ctaLabel, onCtaClick)
          : <Button label={ctaLabel} variant="primary" size="lg" onClick={onCtaClick} />}
      </CtaWrap>
    </Inner>
  </Band>
);
